import { useState } from 'react';
import { NJChatInput, NJIcon, NJIconButton, NJSpinner } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { COREAI_SUGGESTIONS, Size2MixAppShell, Size2MixSecondaryNav, Size2MixSecondaryNavTitle, useSize2MixRefNavigation } from '@shared/size2mix-shell';
import { useSendMessage } from './hooks';
import type { ChatMessage } from './types';

export const meta = {
    title: 'Référence — Nemo CoreAI',
    description: 'Page de référence Nemo CoreAI : nav secondaire « Conversation », barre de titre, titre dégradé IA, 4 cartes de suggestions et NJChatInput (variante ai).',
    tags: ['reference', 'poc', 'po'],
    author: 'dv5886',
    status: 'review' as const,
    category: 'hors-reseau/core-ai',
    createdAt: '2026-10-10',
};

const AI_GRADIENT = 'linear-gradient(90deg, var(--nj-core-color-blue-600), var(--nj-core-color-purple-500), var(--nj-core-color-pink-500))';

export default function RefCoreAI() {
    const nav = useSize2MixRefNavigation();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [draft, setDraft] = useState('');
    const send = useSendMessage((m) => setMessages((prev) => [...prev, m]));

    const submit = (content: string) => {
        if (!content.trim()) return;
        setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content }]);
        setDraft('');
        send.mutate(content);
    };

    return (
        <PrototypeStudioLayout title={meta.title}>
            <Size2MixAppShell
                activePrimary="coreai"
                onPrimaryNavigate={nav.onPrimaryNavigate}
                secondaryNav={
                    <Size2MixSecondaryNav
                        header={<Size2MixSecondaryNavTitle icon={<NJIcon name="engie_ai" />} title="Nemo CoreAI" />}
                        items={[{ id: 'conversation', icon: 'chat_bubble_outline', label: 'Conversation' }]}
                        activeId="conversation"
                        onSelect={() => setMessages([])}
                    />
                }
                contentPadding={0}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 16px 4px 24px', borderBottom: '1px solid var(--nj-core-color-grey-300)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, fontSize: 18 }}>
                        <NJIcon name="engie_ai" />
                        Nemo CoreAI
                    </span>
                    <span style={{ display: 'inline-flex', gap: 4 }}>
                        <NJIconButton icon="add_comment" label="Nouvelle conversation" variant="tertiary" scale="sm" onClick={() => setMessages([])} />
                        <NJIconButton icon="fullscreen" label="Plein écran" variant="tertiary" scale="sm" />
                    </span>
                </div>
                <div style={{ flex: 1, width: '100%', maxWidth: 760, margin: '0 auto', padding: '24px 0 16px', display: 'flex', flexDirection: 'column', gap: 24 }}>
                    {messages.length === 0 ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 56 }}>
                            <h1
                                style={{
                                    margin: 0,
                                    textAlign: 'center',
                                    fontSize: 34,
                                    whiteSpace: 'nowrap',
                                    fontWeight: 700,
                                    background: AI_GRADIENT,
                                    WebkitBackgroundClip: 'text',
                                    backgroundClip: 'text',
                                    color: 'transparent',
                                }}
                            >
                                Comment puis-je vous aider aujourd'hui ?
                            </h1>
                            <div style={{ display: 'flex', gap: 24, marginBottom: 80 }}>
                                {COREAI_SUGGESTIONS.map((s) => (
                                    <button
                                        key={s.text}
                                        type="button"
                                        onClick={() => submit(s.text)}
                                        style={{
                                            width: 146,
                                            height: 246,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 12,
                                            textAlign: 'left',
                                            padding: 24,
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            fontSize: 13,
                                            lineHeight: 1.6,
                                            color: 'var(--nj-semantic-color-text-brand-primary-default)',
                                            background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                                            border: '2px solid var(--nj-semantic-color-border-brand-strong-default)',
                                            borderRadius: 4,
                                        }}
                                    >
                                        <span className="material-icons" style={{ fontSize: 18 }}>
                                            {s.icon}
                                        </span>
                                        {s.text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {messages.map((m) => (
                                <div
                                    key={m.id}
                                    style={{
                                        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                                        maxWidth: '75%',
                                        padding: '10px 14px',
                                        borderRadius: 'var(--nj-semantic-size-border-radius-md)',
                                        background:
                                            m.role === 'user'
                                                ? 'var(--nj-semantic-color-background-neutral-secondary-default)'
                                                : 'var(--nj-semantic-color-background-neutral-primary-default)',
                                        border: m.role === 'assistant' ? '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' : 'none',
                                    }}
                                >
                                    {m.content}
                                </div>
                            ))}
                            {send.isPending && <NJSpinner variant="ai" scale="sm" isLoading />}
                        </div>
                    )}

                    <NJChatInput.Root
                        variant="ai"
                        placeholder="Envoyer un message à notre chat IA."
                        helperText="L'IA peut faire des erreurs. Vérifiez les informations importantes."
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                submit(draft);
                            }
                        }}
                        primary_action={<NJChatInput.Button icon="arrow_upward" label="Envoyer" onClick={() => submit(draft)} />}
                    />
                </div>
            </Size2MixAppShell>
        </PrototypeStudioLayout>
    );
}
